// Crew booking as a full sheet (mobile overhaul Phase B §6.3 G) — the
// mobile render of CrewEntryRow's inline editor and AddCrewBooking, which
// are not rendered at ≤880px. One sheet does both: with a booking it edits
// (the crew member is fixed, as on desktop — reassign by removing and
// re-adding), without one it adds with the crew picker as the first field.
//
// Every rate/date rule is the desktop one: the day rate and half-day rate are
// snapshots, a half day may not be saved without a half-day rate, and the
// half-day selection is reconciled whenever the range shrinks.

import { useState } from 'react';
import { bookingCostCents } from '../../shared/crewCost';
import { DAYS } from '../lib/calendar';
import { formatMoney, parseMoney } from '../lib/moneyFormat';
import {
  crewDateLabel,
  dateRange,
  halfSuggestionCents,
  parseHalfDayDates,
  reconcileHalfDayDates,
} from '../lib/projectDetailShared';
import type { CrewContactRow, ProjectCrewConflict, ProjectCrewWithName } from '../../shared/types';
import { AlertIcon, TrashIcon } from './icons';
import { DateSheet } from './DateSheet';
import { PickerSheet } from './PickerSheet';
import { Sheet, SheetButton, SheetRow } from './Sheet';

export interface BookingDraftFields {
  crew_id: string;
  role: string | null;
  day_rate_cents: number | null;
  start_date: string;
  end_date: string;
  half_day_dates: string[];
  half_day_rate_cents: number | null;
}

export function ProjectDetailBookingSheet({
  booking,
  register,
  conflicts,
  projectName,
  today,
  onSave,
  onAdd,
  onRemove,
  onClose,
}: {
  /** null = the add flow (crew picker first, empty rates). */
  booking: ProjectCrewWithName | null;
  register: CrewContactRow[];
  conflicts: ProjectCrewConflict[];
  projectName: string;
  today: string;
  onSave: (fields: Record<string, unknown>) => Promise<void>;
  onAdd: (fields: BookingDraftFields) => Promise<void>;
  onRemove: () => Promise<void> | void;
  onClose: () => void;
}) {
  const editing = booking !== null;
  const [crewId, setCrewId] = useState<string | null>(booking?.crew_id ?? null);
  const [role, setRole] = useState(booking?.role ?? '');
  const [rate, setRate] = useState(booking === null ? '' : (booking.day_rate_cents / 100).toString());
  const [start, setStart] = useState<string>(booking?.start_date ?? today);
  const [end, setEnd] = useState<string>(booking?.end_date ?? today);
  const [halfDays, setHalfDays] = useState<Set<string>>(
    () => new Set(parseHalfDayDates(booking?.half_day_dates ?? null)),
  );
  const [halfRate, setHalfRate] = useState(
    booking?.half_day_rate_cents == null ? '' : (booking.half_day_rate_cents / 100).toString(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sub, setSub] = useState<'crew' | 'start' | 'end' | 'remove' | null>(null);

  const selectedName = editing
    ? booking.crew_name
    : (register.find((r) => r.id === crewId)?.name ?? null);
  const halfRateMissing = halfDays.size > 0 && halfRate.trim() === '';
  const canSave = crewId !== null && !busy && !halfRateMissing && (!editing || rate.trim() !== '');

  /** A shrinking range must not leave a half day outside it (the server
      rejects those), so every date change reconciles the selection. */
  const setRange = (nextStart: string, nextEnd: string) => {
    setStart(nextStart);
    setEnd(nextEnd);
    setHalfDays((prev) => new Set(reconcileHalfDayDates([...prev], nextStart, nextEnd)));
  };

  const toggleHalfDay = (date: string) => {
    setHalfDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const submit = async () => {
    if (!canSave || crewId === null) return;
    setBusy(true);
    setError(null);
    try {
      const fields = {
        role: role.trim() || null,
        day_rate_cents: rate.trim() === '' ? null : parseMoney(rate),
        start_date: start,
        end_date: end,
        half_day_dates: [...halfDays],
        half_day_rate_cents: halfRate.trim() === '' ? null : parseMoney(halfRate),
      };
      if (editing) await onSave(fields);
      else await onAdd({ crew_id: crewId, ...fields });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the booking.');
      setBusy(false);
    }
  };

  // The live cost line uses the same split the saved row will — a draft that
  // reads "$1,050 · 1 day + 1 half" costs exactly that once saved.
  const preview = bookingCostCents({
    start_date: start,
    end_date: end,
    day_rate_cents: parseMoney(rate) ?? 0,
    half_day_rate_cents: halfRate.trim() === '' ? null : parseMoney(halfRate),
    half_day_dates: JSON.stringify([...halfDays]),
  });
  const days = dateRange(start, end);
  const suggestion = halfSuggestionCents(rate);

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        variant="full"
        title={editing ? 'Edit booking' : 'Add crew'}
        context={selectedName ? `${selectedName} · ${projectName}` : projectName}
        leftAction={<SheetButton onClick={onClose}>Cancel</SheetButton>}
        rightAction={
          <SheetButton tone="accent" disabled={!canSave} onClick={submit}>
            {busy ? 'Saving…' : 'Save'}
          </SheetButton>
        }
        className="pd-booking"
      >
        <div className="pd-form">
          <div className="f-field">
            <span className="f-label">Crew member</span>
            {editing ? (
              <div className="f-select is-static">{selectedName}</div>
            ) : (
              <button
                type="button"
                className={`f-select${selectedName ? '' : ' is-empty'}`}
                onClick={() => setSub('crew')}
              >
                <span>{selectedName ?? 'Select…'}</span>
                <span className="caret">▾</span>
              </button>
            )}
          </div>

          <div className="pd-form-pair">
            <div className="f-field">
              <span className="f-label">Start</span>
              <button type="button" className="f-select mono" onClick={() => setSub('start')}>
                <span>{crewDateLabel(start, start)}</span>
                <span className="caret">▾</span>
              </button>
            </div>
            <div className="f-field">
              <span className="f-label">End</span>
              <button type="button" className="f-select mono" onClick={() => setSub('end')}>
                <span>{crewDateLabel(end, end)}</span>
                <span className="caret">▾</span>
              </button>
            </div>
          </div>

          <div className="f-field">
            <span className="f-label">Role</span>
            <input
              className="f-input"
              value={role}
              placeholder="DP, sound…"
              onChange={(e) => setRole(e.target.value)}
            />
          </div>

          {days.length > 0 && (
            <div className="f-field">
              <span className="f-label">
                Days <span className="pd-form-hint">· tap to toggle full / half</span>
              </span>
              <div className="pd-daychips">
                {days.map((d) => {
                  const half = halfDays.has(d);
                  return (
                    <button
                      type="button"
                      key={d}
                      className={`pd-dc ${half ? 'half' : 'full'}`}
                      aria-pressed={half}
                      onClick={() => toggleHalfDay(d)}
                    >
                      <b>{DAYS[new Date(`${d}T00:00:00`).getDay()]}</b>
                      <span>
                        {Number(d.slice(8, 10))}
                        {half && ' ½'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pd-form-pair">
            <div className="f-field">
              <span className="f-label">Day rate</span>
              <input
                className="f-input money"
                inputMode="decimal"
                value={rate}
                placeholder="$0"
                onChange={(e) => setRate(e.target.value)}
              />
            </div>
            <div className="f-field">
              <span className="f-label">
                Half-day rate {halfRateMissing && <span className="req">*</span>}
              </span>
              <input
                className="f-input money"
                inputMode="decimal"
                value={halfRate}
                placeholder={suggestion !== null ? `suggested: ${formatMoney(suggestion)}` : '$0'}
                onChange={(e) => setHalfRate(e.target.value)}
              />
            </div>
          </div>

          {preview !== null && (
            <div className="pd-form-cost">
              {formatMoney(preview.total_cents)} · {preview.full_days} day
              {preview.full_days === 1 ? '' : 's'}
              {preview.half_days > 0 &&
                ` + ${preview.half_days} half${preview.half_days === 1 ? '' : 'ves'}`}
            </div>
          )}
          {halfRateMissing && (
            <div className="pd-warn">A half day needs a half-day rate before it can be saved.</div>
          )}
          {error && <p className="error-text">{error}</p>}
        </div>

        {conflicts.length > 0 && (
          <div className="pd-booking-foot">
            {conflicts.map((cf, i) => (
              <div className="mrow danger" key={i}>
                <span className="mrow-ico">
                  <AlertIcon />
                </span>
                <span className="mrow-l">
                  <span className="mrow-label">Double-booked</span>
                  <small>
                    Also on {cf.project_name}, {crewDateLabel(cf.start_date, cf.end_date)}
                  </small>
                </span>
              </div>
            ))}
          </div>
        )}
        {editing && (
          <SheetRow
            icon={<TrashIcon />}
            label="Remove booking"
            tone="danger"
            onSelect={() => setSub('remove')}
          />
        )}
      </Sheet>

      {sub === 'crew' && (
        <PickerSheet
          open
          title="Crew member"
          searchable
          items={register.map((r) => ({
            value: r.id,
            label: r.name,
            sub: r.day_rate_cents == null ? undefined : `${formatMoney(r.day_rate_cents)}/day`,
          }))}
          value={crewId}
          onClose={() => setSub(null)}
          onSelect={(id) => {
            setCrewId(id);
            // Rates snapshot from the register, exactly as the desktop add
            // flow does — an empty register rate leaves the field empty.
            const contact = register.find((r) => r.id === id);
            setRate(contact?.day_rate_cents != null ? (contact.day_rate_cents / 100).toString() : '');
            setHalfRate(
              contact?.half_day_rate_cents != null ? (contact.half_day_rate_cents / 100).toString() : '',
            );
          }}
        />
      )}
      {sub === 'start' && (
        <DateSheet
          open
          title="First day"
          context={selectedName ? `${selectedName} · ${projectName}` : projectName}
          value={start}
          onClose={() => setSub(null)}
          onPick={(iso) => {
            if (!iso) return;
            setRange(iso, iso > end ? iso : end);
          }}
        />
      )}
      {sub === 'end' && (
        <DateSheet
          open
          title="Last day"
          context={selectedName ? `${selectedName} · ${projectName}` : projectName}
          value={end}
          seed={start}
          onClose={() => setSub(null)}
          onPick={(iso) => {
            if (!iso) return;
            setRange(iso < start ? iso : start, iso);
          }}
        />
      )}
      {sub === 'remove' && (
        <Sheet
          open
          onClose={() => setSub(null)}
          variant="half"
          title="Remove booking?"
          context={selectedName ? `${selectedName} · ${projectName}` : projectName}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setSub(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={async () => {
                  await onRemove();
                  onClose();
                }}
              >
                Remove booking
              </button>
            </>
          }
        >
          <p className="pd-sheet-copy">
            The booking comes off this project. The crew member stays in the register.
          </p>
        </Sheet>
      )}
    </>
  );
}
